import { useEffect, useState } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import { datadogRum } from '@datadog/browser-rum'

// One Stripe load per page. This used to run inside Loader's render, which
// asked for a fresh Stripe instance every time a Loader rendered.
const loading = loadStripe(process.env.STRIPE_PUBLISHABLE_KEY)

// What <Elements> is given. A failed load RESOLVES null instead of rejecting:
// react-stripe-js reads null as "no Stripe", where a rejection went unhandled
// on every page that mounts a Loader.
export const stripePromise = loading.catch(() => null)

// react-stripe-js shows no difference between "Stripe is still loading" and
// "Stripe never will", so the payment fields ask this instead. loadStripe's
// contract is Promise<Stripe | null>: a load that yields no instance is a
// failure too, whether it rejected or resolved null (today stripe-js resolves
// null only when imported server side, but the contract allows it anywhere).
const loadFailure = loading.then(
  (stripe) => (stripe ? null : new Error('Stripe.js loaded without a Stripe instance')),
  (error) => error,
)

export function useStripeLoadFailure() {
  const [failure, setFailure] = useState(null)

  useEffect(() => {
    let mounted = true
    loadFailure.then((error) => {
      if (mounted && error) { setFailure(error) }
    })
    return () => { mounted = false }
  }, [])

  return failure
}

// Sources already reported on this page load. The checkout form remounts when
// the cart fetch resolves, so without this one outage reports twice per field.
const reportedSources = new Set()

// One report per degraded field per page view, so RUM counts outages rather
// than re-renders. RUM only initialises when window.datadogRumSampleRate > 0
// (scripts/datadog.js), so this is inert wherever RUM is off. Guarded so the
// report can never break the fallback it is reporting on.
export function reportPaymentUnavailable(source, error) {
  if (reportedSources.has(source)) { return }
  reportedSources.add(source)

  try {
    const reported = error instanceof Error ? error : new Error(error?.message || 'Stripe unavailable')
    datadogRum.addError(reported, { source, stripeErrorType: error?.type })
  } catch (telemetryError) {
    // Nothing to fall back to: the customer already has the notice.
  }
}
